import { Slot } from "@radix-ui/react-slot";
import { cva, type VariantProps } from "class-variance-authority";
import * as React from "react";
import { cn } from "@/lib/utils";

const buttonVariants = cva(
  cn(
    "inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md text-sm font-medium",
    "transition-colors outline-none disabled:pointer-events-none disabled:opacity-50",
    "[&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0",
    "focus-visible:[box-shadow:var(--rd-shadow-focus)]"
  ),
  {
    variants: {
      variant: {
        primary:
          "bg-brand-600 text-white border border-brand-600 hover:bg-brand-700 hover:border-brand-700",
        default:
          "bg-brand-600 text-white border border-brand-600 hover:bg-brand-700 hover:border-brand-700",
        secondary:
          "bg-bg-surface text-text-primary border border-border-subtle hover:bg-bg-muted-rd",
        ghost: "bg-transparent text-text-primary hover:bg-bg-muted-rd",
        danger: "bg-rd-danger-500 text-white hover:bg-rd-danger-600",
        destructive: "bg-rd-danger-500 text-white hover:bg-rd-danger-600",
        outline: "border border-border-subtle bg-bg-surface text-text-primary hover:bg-bg-muted-rd",
        link: "text-brand-600 underline-offset-4 hover:underline",
      },
      size: {
        default: "h-9 px-3.5 py-0 rounded-lg text-[13px]",
        sm: "h-[30px] px-2.5 rounded-md text-[12px]",
        lg: "h-11 rounded-lg px-8",
        icon: "h-9 w-9 p-0 rounded-lg",
      },
    },
    defaultVariants: { variant: "primary", size: "default" },
  }
);

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>, VariantProps<typeof buttonVariants> {
  asChild?: boolean;
}

export const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, asChild = false, ...props }, ref) => {
    const Comp = asChild ? Slot : "button";
    return (
      <Comp className={cn(buttonVariants({ variant, size, className }))} ref={ref} {...props} />
    );
  }
);
Button.displayName = "Button";

export { buttonVariants };
